import { useCallback, useMemo, useState } from 'react'
import { useSnackbar } from 'notistack'

import {
  DGEImportLicensePreview,
  DGEImportLicensesPreviewResponse
} from '@img-arena/consoles-api-streaming'
import { Box, Stack } from '@mui/material'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import {
  EXAMPLE_IMPORT_LICENSES_CSV_DATA,
  EXAMPLE_IMPORT_LICENSES_CSV_HEADERS
} from '@/components/forms/ImportLicenses/streaming/ImportLicensesForm.helpers.dge'
import {
  MAX_YEAR,
  MIN_YEAR
} from '@/components/inputs/LicensingPeriods/streaming/LicensingPeriod.dge'
import { streamingRestApi } from '@/service/streamingApi'
import { FormDialogProps } from '@/types/forms'
import downloadCSV from '@/utils/_legacy/downloadCSV'

import { ImportedLicenseAccordion } from './ImportedLicenseAccordion.dge'
import { ImportLicensesPreviewStep } from './ImportLicensesPreviewStep.dge'
import { UpdatedPeriod } from './types'
import { transformDataForApprove } from './utils'

type Data = {
  operatorId: number
}

export type DGEImportLicensesFormProps = FormDialogProps<DGEImportLicensesPreviewResponse, Data>

export const DGEImportLicensesForm = ({
  onClose,
  onSubmit,
  open = false,
  data
}: DGEImportLicensesFormProps) => {
  const { enqueueSnackbar } = useSnackbar()

  const [isUploading, setIsUploading] = useState(false)
  const [previewData, setPreviewData] = useState<DGEImportLicensesPreviewResponse | null>(null)
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false)
  const [expandedPeriods, setExpandedPeriods] = useState<Set<string>>(new Set())
  const [editableData, setEditableData] = useState<DGEImportLicensesPreviewResponse | null>(null)
  const [selectedLicenses, setSelectedLicenses] = useState<Set<number>>(new Set())
  const [selectedProperties, setSelectedProperties] = useState<Map<number, number>>(new Map())
  const [selectedPropertyNames, setSelectedPropertyNames] = useState<Map<number, string>>(new Map())

  const handleClose = () => {
    setPreviewData(null)
    setEditableData(null)
    setExpandedAccordion(false)
    setExpandedPeriods(new Set())
    setSelectedLicenses(new Set())
    setSelectedProperties(new Map())
    setSelectedPropertyNames(new Map())
    onClose?.()
  }

  const handleAccordionChange = (panel: string, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? panel : false)
  }

  const handlePeriodExpandedChange = (
    licenseIndex: number,
    periodIndex: number,
    expanded: boolean
  ) => {
    const periodId = `license-${licenseIndex}-period-${periodIndex}`
    setExpandedPeriods((prev) => {
      const newSet = new Set(prev)
      if (expanded) {
        newSet.add(periodId)
      } else {
        newSet.delete(periodId)
      }
      return newSet
    })
  }

  const handleAutoBookingChange = (licenseIndex: number, autoBooking: boolean) => {
    if (!editableData) return

    setEditableData((prev) => {
      if (!prev) return prev
      const newData = { ...prev }
      if (newData.licenses && newData.licenses[licenseIndex]) {
        newData.licenses[licenseIndex] = {
          ...newData.licenses[licenseIndex],
          isAutomaticBooking: autoBooking
        }
      }
      return newData
    })
  }

  const handlePeriodChange = (
    licenseIndex: number,
    periodIndex: number,
    updatedPeriod: UpdatedPeriod
  ) => {
    if (!editableData) return

    setEditableData((prev) => {
      if (!prev) return prev
      const newData = { ...prev }
      if (
        newData.licenses &&
        newData.licenses[licenseIndex] &&
        newData.licenses[licenseIndex].licenseDates
      ) {
        const newLicenses = [...newData.licenses]
        const newLicense = { ...newLicenses[licenseIndex] }
        const newPeriods = [...newLicense.licenseDates]
        newPeriods[periodIndex] = {
          ...newPeriods[periodIndex],
          ...updatedPeriod
        }
        newLicense.licenseDates = newPeriods
        newLicenses[licenseIndex] = newLicense
        newData.licenses = newLicenses
      }
      return newData
    })
  }

  const handleDeletePeriod = (licenseIndex: number, periodIndex: number) => {
    if (!editableData) return

    setEditableData((prev) => {
      if (!prev) return prev
      const newData = { ...prev }
      if (
        newData.licenses &&
        newData.licenses[licenseIndex] &&
        newData.licenses[licenseIndex].licenseDates
      ) {
        const newLicenses = [...newData.licenses]
        const newLicense = { ...newLicenses[licenseIndex] }
        const newPeriods = newLicense.licenseDates.filter((_, index) => index !== periodIndex)
        newLicense.licenseDates = newPeriods
        newLicenses[licenseIndex] = newLicense
        newData.licenses = newLicenses
      }
      return newData
    })
  }

  const handleLicenseSelection = (licenseIndex: number, selected: boolean) => {
    setSelectedLicenses((prev) => {
      const newSet = new Set(prev)
      if (selected) {
        newSet.add(licenseIndex)
      } else {
        newSet.delete(licenseIndex)
      }
      return newSet
    })
  }

  const handlePropertyChange = (licenseIndex: number, propertyId: number | null) => {
    setSelectedProperties((prev) => {
      const newMap = new Map(prev)
      if (propertyId) {
        newMap.set(licenseIndex, propertyId)

        streamingRestApi.endpoints.operators
          .getLicensesProperties()
          .then((response) => {
            const property = response.data.content.find(
              (p: { id: number; name: string }) => p.id === propertyId
            )
            if (property) {
              setSelectedPropertyNames((prevNames) => {
                const newNamesMap = new Map(prevNames)
                newNamesMap.set(licenseIndex, property.name)
                return newNamesMap
              })
            }
          })
          .catch(() => {
            setSelectedPropertyNames((prevNames) => {
              const newNamesMap = new Map(prevNames)
              newNamesMap.set(licenseIndex, `Property ${propertyId}`)
              return newNamesMap
            })
          })
      } else {
        newMap.delete(licenseIndex)
        setSelectedPropertyNames((prevNames) => {
          const newNamesMap = new Map(prevNames)
          newNamesMap.delete(licenseIndex)
          return newNamesMap
        })
      }
      return newMap
    })
  }

  const handlePreviewData = (previewData: DGEImportLicensesPreviewResponse) => {
    setPreviewData(previewData)
    setEditableData(previewData)
    if (previewData?.licenses && previewData.licenses.length > 0) {
      const { valid } = validateLicenses(previewData.licenses, new Map())
      setSelectedLicenses(new Set(valid))
      setExpandedAccordion('panel-0')
    }
  }

  const handleSubmit = async () => {
    if (!editableData || selectedLicenses.size === 0 || !data?.operatorId) return

    const selectedLicensesData =
      editableData.licenses?.filter((_, index) => selectedLicenses.has(index)) || []
    const { invalid } = validateLicenses(selectedLicensesData, selectedProperties)

    if (invalid.length > 0) {
      enqueueSnackbar(
        `Cannot import licenses: ${invalid.length} selected license(s) have missing property data or invalid years (must be between 2010-2050)`,
        { variant: 'error' }
      )
      return
    }

    setIsUploading(true)

    try {
      const transformedData = transformDataForApprove(
        editableData,
        selectedLicenses,
        selectedProperties,
        selectedPropertyNames
      )

      await streamingRestApi.endpoints.operators.importLicensesApprove(
        data.operatorId,
        transformedData
      )

      enqueueSnackbar('Licenses imported successfully', { variant: 'success' })
      onSubmit?.()
      handleClose()
    } catch (error) {
      enqueueSnackbar('Failed to import licenses', { variant: 'error' })
    } finally {
      setIsUploading(false)
    }
  }

  const handleDownloadExample = useCallback(() => {
    downloadCSV(
      { filename: 'example_licenses_import' },
      EXAMPLE_IMPORT_LICENSES_CSV_DATA,
      EXAMPLE_IMPORT_LICENSES_CSV_HEADERS
    )
  }, [])

  const validateLicenses = (
    licenses: DGEImportLicensePreview[],
    selectedProperties: Map<number, number>
  ): { valid: number[]; invalid: number[] } => {
    const valid: number[] = []
    const invalid: number[] = []

    licenses.forEach((license, index) => {
      const hasOriginalProperty = license.property?.id && license.property?.name
      const hasSelectedProperty = selectedProperties.has(index)

      const hasValidProperty = hasOriginalProperty || hasSelectedProperty

      const hasValidYears =
        license.licenseDates?.every((period) => {
          const startYear = new Date(period.startDate).getFullYear()
          const endYear = new Date(period.endDate).getFullYear()
          return (
            startYear >= MIN_YEAR &&
            startYear <= MAX_YEAR &&
            endYear >= MIN_YEAR &&
            endYear <= MAX_YEAR
          )
        }) ?? true

      if (hasValidProperty && hasValidYears) {
        valid.push(index)
      } else {
        invalid.push(index)
      }
    })

    return { valid, invalid }
  }

  const hasInvalidLicenses = useMemo(() => {
    if (!editableData?.licenses || selectedLicenses.size === 0) return false

    const selectedLicensesData = editableData.licenses.filter((_, index) =>
      selectedLicenses.has(index)
    )
    const { invalid } = validateLicenses(selectedLicensesData, selectedProperties)
    return invalid.length > 0
  }, [editableData, selectedLicenses, selectedProperties])

  return (
    <ConsoleDialog
      open={open}
      title="Import Licenses"
      description="Upload a CSV file containing license data to import multiple licenses at once."
      onClose={handleClose}
      dialogSize={false}
      width={820}
      smallTopMargin
      ActionButtonProps={{
        text: 'Download Example.csv',
        onClick: handleDownloadExample
      }}
      SaveButtonProps={{
        text: `Import Licenses (${selectedLicenses.size} selected)`,
        disabled: !editableData || isUploading || selectedLicenses.size === 0 || hasInvalidLicenses,
        onClick: handleSubmit
      }}
    >
      <Stack direction="column" spacing={3}>
        {!previewData && (
          <ImportLicensesPreviewStep onPreviewData={handlePreviewData} data={data} />
        )}

        {editableData && (
          <Box>
            {editableData?.licenses?.map((license: DGEImportLicensePreview, index: number) => {
              const excludedProperties = Array.from(selectedProperties.values()).filter(
                (_, propIndex) => propIndex !== index
              )

              return (
                <ImportedLicenseAccordion
                  key={index}
                  license={license}
                  index={index}
                  isSelected={selectedLicenses.has(index)}
                  isExpanded={expandedAccordion === `panel-${index}`}
                  expandedPeriods={expandedPeriods}
                  selectedProperty={selectedProperties.get(index)}
                  selectedPropertyName={selectedPropertyNames.get(index)}
                  excludedProperties={excludedProperties}
                  onSelectionChange={handleLicenseSelection}
                  onAccordionChange={(index, expanded) =>
                    handleAccordionChange(`panel-${index}`, expanded)
                  }
                  onAutoBookingChange={handleAutoBookingChange}
                  onPeriodExpandedChange={handlePeriodExpandedChange}
                  onPeriodChange={handlePeriodChange}
                  onDeletePeriod={handleDeletePeriod}
                  onPropertyChange={handlePropertyChange}
                />
              )
            })}
          </Box>
        )}
      </Stack>
    </ConsoleDialog>
  )
}
